import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Home, Smile, HeartHandshake, MessageCircle, Users, Settings, UserCircle, Menu, X, Gamepad2, Leaf, Star, LogOut, User, Heart, Sparkles } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import Avatar from './Avatar';

const Navbar = ({ user, onLogout }) => {
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = React.useState(false);
  const { isDark } = useTheme();

  const toggleMenu = React.useCallback(() => {
    setIsMenuOpen(prev => !prev);
  }, []);

  const navigation = [
    { name: 'Dashboard', href: '/dashboard', icon: Home, gradient: 'from-blue-500 to-indigo-500' },
    { name: 'Messages', href: '/messages', icon: MessageCircle, gradient: 'from-emerald-500 to-green-500' },
  ];

  const mobileOnlyNavigation = [
    { name: 'Mood Tracker', href: '/mood', icon: Smile, gradient: 'from-purple-500 to-pink-500' },
    { name: 'Matches', href: '/matches', icon: HeartHandshake, gradient: 'from-pink-500 to-rose-500' },
    { name: 'Favorites', href: '/matches', state: { defaultTab: 'favorites' }, icon: Star, gradient: 'from-amber-500 to-orange-500' },
    { name: 'Support Groups', href: '/groups', icon: Users, gradient: 'from-blue-500 to-cyan-500' },
    { name: 'AI Counsellor', href: '/counselor', icon: Sparkles, gradient: 'from-teal-500 to-emerald-500' },
    { name: 'Relax Games', href: '/relax', icon: Gamepad2, gradient: 'from-violet-500 to-purple-500' },
    { name: 'Wellness', href: '/wellness', icon: Leaf, gradient: 'from-green-500 to-emerald-500' },
    { name: 'Profile', href: '/profile', icon: UserCircle, gradient: 'from-indigo-500 to-blue-500' },
    { name: 'Settings', href: '/settings', icon: Settings, gradient: 'from-slate-500 to-gray-500' },
  ];

  return (
    <nav className="sticky top-0 z-50 bg-white dark:bg-slate-900 shadow-md border-b border-emerald-200/50 dark:border-slate-700">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16 relative z-50 bg-white dark:bg-slate-900">
          {/* Logo */}
          <Link to="/dashboard" className="flex-shrink-0 flex items-center">
            <img 
              src="/logo.png" 
              alt="eternitysingles" 
              className="h-12 w-auto"
              style={{ maxWidth: '300px', imageRendering: '-webkit-optimize-contrast' }}
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-4 relative z-30">
            {navigation.map((item) => {
              const IconComponent = item.icon;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  state={item.state}
                  className={`nav-link flex items-center space-x-2 ${
                    location.pathname === item.href && !item.state ? 'active' : ''
                  }`}
                  data-testid={`nav-${item.name.toLowerCase().replace(' ', '-')}`}
                >
                  <IconComponent className="w-5 h-5" />
                  <span className="text-base font-semibold">{item.name}</span>
                </Link>
              );
            })}
          </div>

          {/* User Menu */}
          <div className="hidden md:flex items-center relative z-40">
            <button
              onClick={() => setIsProfileMenuOpen(!isProfileMenuOpen)}
              className="flex items-center space-x-3 hover:bg-emerald-50 dark:hover:bg-slate-800 rounded-lg px-3 py-2 transition-colors"
            >
              <Avatar
                src={user?.profile_image}
                name={user?.full_name || user?.username}
                sizeClass="w-8 h-8"
                textClass="text-xs"
                testId="navbar-avatar"
              />
              <span className="text-sm font-medium text-emerald-700 dark:text-emerald-300">Hi, {user?.full_name || user?.username}</span>
            </button>

            {/* Profile Dropdown Menu */}
            {isProfileMenuOpen && (
              <>
                <div 
                  className="fixed inset-0 z-30" 
                  onClick={() => setIsProfileMenuOpen(false)}
                />
                
                <div className="absolute right-0 top-full mt-2 w-56 bg-white dark:bg-slate-800 border border-emerald-200 dark:border-slate-700 rounded-xl shadow-2xl z-50 overflow-hidden">
                  <Link
                    to="/profile"
                    onClick={() => setIsProfileMenuOpen(false)}
                    className="w-full px-4 py-3 text-left hover:bg-emerald-50 dark:hover:bg-slate-700 transition-colors flex items-center space-x-3 border-b border-emerald-100 dark:border-slate-700"
                  >
                    <UserCircle className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                    <span className="text-emerald-800 dark:text-emerald-100 font-medium">Profile</span>
                  </Link>
                  
                  <Link
                    to="/settings"
                    onClick={() => setIsProfileMenuOpen(false)}
                    className="w-full px-4 py-3 text-left hover:bg-emerald-50 dark:hover:bg-slate-700 transition-colors flex items-center space-x-3 border-b border-emerald-100 dark:border-slate-700"
                  >
                    <Settings className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                    <span className="text-emerald-800 dark:text-emerald-100 font-medium">Settings</span>
                  </Link>
                  
                  <button
                    onClick={() => {
                      setIsProfileMenuOpen(false);
                      onLogout();
                    }}
                    className="w-full px-4 py-3 text-left hover:bg-emerald-50 dark:hover:bg-emerald-900/20 transition-colors flex items-center space-x-3"
                  >
                    <LogOut className="w-5 h-5 text-emerald-600" />
                    <span className="text-emerald-600 font-medium">Logout</span>
                  </button>
                </div>
              </>
            )}
          </div>

          {/* Mobile menu button */}
          <button
            onClick={toggleMenu}
            className="md:hidden flex items-center justify-center w-10 h-10 rounded-lg hover:bg-emerald-100 dark:hover:bg-slate-700 transition-colors"
            aria-label="Toggle menu"
            data-testid="mobile-menu-btn"
          >
            {isMenuOpen ? (
              <X className="w-6 h-6 text-emerald-700 dark:text-emerald-300" />
            ) : (
              <Menu className="w-6 h-6 text-emerald-700 dark:text-emerald-300" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Navigation */}
      {isMenuOpen && (
        <>
          {/* Overlay */}
          <div 
            className="md:hidden fixed inset-0 bg-black bg-opacity-50 z-30"
            onClick={toggleMenu}
          />
          <div className="md:hidden fixed left-0 right-0 top-16 bottom-0 bg-gradient-to-br from-emerald-50 via-green-50 to-teal-50 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900 z-40 overflow-y-auto border-t border-emerald-200/50 dark:border-slate-600/50 shadow-2xl">
            <div className="px-2 pt-2 pb-6 space-y-2">
              {[...navigation, ...mobileOnlyNavigation].map((item) => {
                const IconComponent = item.icon;
                const isActive = location.pathname === item.href && !item.state;
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    state={item.state}
                    className={`group relative overflow-hidden flex items-center space-x-3 px-4 py-3 rounded-xl transition-all shadow-md hover:shadow-lg ${
                      isActive 
                        ? `bg-gradient-to-r ${item.gradient} text-white scale-[1.02]` 
                        : 'bg-white dark:bg-slate-800 text-emerald-700 dark:text-emerald-300 hover:scale-[1.02]'
                    }`}
                    onClick={toggleMenu}
                    data-testid={`mobile-nav-${item.name.toLowerCase().replace(' ', '-')}`}
                  >
                    {isActive && (
                      <div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-10 transition-opacity"></div>
                    )}
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      isActive ? 'bg-white/20' : `bg-gradient-to-br ${item.gradient}`
                    }`}>
                      <IconComponent className={`w-5 h-5 ${ isActive ? 'text-white' : 'text-white'}`} strokeWidth={2} />
                    </div>
                    <span className="font-semibold">{item.name}</span>
                  </Link>
                );
              })}
            
              {/* Mobile user info and logout */}
              <div className="border-t border-emerald-200/50 dark:border-slate-600/50 pt-3 mt-3 px-2">
                <div className="flex items-center space-x-3 px-2 py-2 mb-3">
                  <Avatar
                    src={user?.profile_image}
                    name={user?.full_name || user?.username}
                    sizeClass="w-10 h-10"
                    textClass="text-sm"
                    testId="navbar-mobile-avatar"
                  />
                  <span className="text-sm font-medium text-emerald-700 dark:text-emerald-300">{user?.full_name || user?.username}</span>
                </div>
                <button
                  onClick={() => {
                    onLogout();
                    toggleMenu();
                  }}
                  className="w-full bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 text-white font-semibold py-3 px-4 rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center space-x-2"
                  data-testid="mobile-logout-btn"
                >
                  <LogOut className="w-5 h-5" />
                  <span>Logout</span>
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </nav>
  );
};

export default Navbar;
